import { hsl, rgb } from '@visx/vendor/d3-color';

const RESERVED_COLORS = {
    other: rgb('#887F8F').formatRgb(),
};
const categoricalPalettes = {
    vibrantMix: [
        '#545CF8', // Blue-purple (medium)
        '#F79009', // Orange (bright)
        '#904EA6', // Purple (dark)
        '#63B512', // Green (bright)
        '#F0384A', // Red (bright)
        '#00BFA5', // Teal (medium)
        '#EF798A', // Pink (light)
        '#4EBC90', // Green (medium)
    ],
    modernDashboard: [
        '#424548', // Dark gray
        '#F3A712', // Orange
        '#5B35D5', // Dark purple
        '#43AA8B', // Teal
        '#E54F6D', // Red-pink
        '#1E88E5', // Blue
        '#FFD166', // Yellow
        '#6C757D', // Medium gray
        '#E8EAED', // Light gray
    ],
    softSpectrum: [
        '#1E4FC2', // Dark blue
        '#E6C84B', // Gold
        '#8b35db', // Purple (medium)
        '#449C6E', // Forest green
        '#dd565a', // Coral red
        '#7FD1FF', // Light blue
        '#A3DF50', // Lime green
        '#6c7d9f', // Gray-blue
        '#F19095', // Pink
        '#4789d5', // Medium blue
        '#e8b758', // Amber
        '#B4A7E5', // Lavender
    ],
} as const;

const categoricalPaletteOptions = [
    { label: 'Vibrant Mix', value: 'vibrantMix' },
    { label: 'Modern Dashboard', value: 'modernDashboard' },
    { label: 'Soft Spectrum', value: 'softSpectrum' },
] as { label: string; value: keyof typeof categoricalPalettes }[];

const adjustments = [
    [0, 0, 0],
    [15, -0.2, 0],
    [0, -0.1, 0.15],
    [0, 0.1, -0.2],
] as const;

const createOrdinalPalette = (
    count: number,
    paletteName: keyof typeof categoricalPalettes = 'vibrantMix',
): string[] => {
    const colors = categoricalPalettes[paletteName] ?? categoricalPalettes.vibrantMix;
    if (count <= 0) return [];

    const palette: string[] = [];
    const baseLength = colors.length;

    if (count <= baseLength) {
        return colors.slice(0, count);
    }

    const cycles = Math.ceil(count / baseLength);

    for (let cycle = 0; cycle < cycles; cycle++) {
        const cycleLength = cycle * baseLength < count ? baseLength : count % (cycle - 1 * baseLength);

        for (let i = 0; i < cycleLength; i++) {
            const color = hsl(colors[i]!);
            const [h, s, l] = adjustments[cycle] ?? [0, 0, 0];
            const hue = color.h + h;
            const saturation = Math.min(Math.max(color.s + s, 0), 1);
            const lightness = Math.min(Math.max(color.l + l, 0), 1);
            color.h = hue > 360 ? hue % 360 : hue;
            color.s = saturation;
            color.l = lightness;
            palette.push(color.formatRgb());
        }
    }

    return palette;
};

export { categoricalPaletteOptions, categoricalPalettes, createOrdinalPalette, RESERVED_COLORS };
